import { useEffect } from "react";
import { GFG_LOGO_HIRES } from "@/constants/campaignBuilder";
import type { GiftLinkUnavailableReason } from "@/interfaces/redemption";

/** Gift-box art above the headline — decorative only (alt="", aria-hidden). */
const GIFTBOX_SRC =
  "https://cdn.shopify.com/s/files/1/1826/6559/files/giftbox_transparent_nohalo.gif?v=1784087573";

/** Judd-approved copy per case (GFG-300 thread, 2026-09-25) — one template for every non-flow link state. */
const COPY: Record<GiftLinkUnavailableReason, { headline: string; body: string }> = {
  "already-redeemed": {
    headline: "This gift has already been redeemed",
    body: "If that wasn’t you, the person who sent it can help sort this out.",
  },
  expired: {
    headline: "This gift has expired",
    body: "If that doesn’t seem right, the person who sent you the gift can help sort this out.",
  },
  "limit-reached": {
    headline: "All the gifts have been claimed",
    body: "This link has reached its limit, so there are no gifts left to redeem. The person who shared it can let you know if more become available.",
  },
  disabled: {
    headline: "This gift link has been deactivated",
    body: "If that doesn’t seem right, the person who sent you the gift can help sort this out.",
  },
};

interface GiftLinkUnavailableProps {
  reason: GiftLinkUnavailableReason;
}

/**
 * Terminus for a real link that can't open the flow — already redeemed (API 410) or expired (API 404) —
 * pixel port of Judd's `gift-already-redeemed.html`. Not a page (no route) — `Redemption.tsx` renders it in place of the flow.
 */
export default function GiftLinkUnavailable({ reason }: GiftLinkUnavailableProps) {
  const { headline, body } = COPY[reason];

  // noindex while this terminus is mounted — the token in the URL must never get indexed.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = `${headline} — Gifts for Good`;
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, [headline]);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex shrink-0 items-center justify-center gap-4 border-card-border border-b bg-white px-6 py-3.5 max-[600px]:px-4 max-[600px]:py-2.5">
        <img
          className="h-11 w-auto object-contain max-[600px]:h-8"
          src={GFG_LOGO_HIRES}
          alt="Gifts for Good"
        />
      </header>

      <main className="flex flex-1 items-center justify-center bg-light-dust px-6 pt-12 pb-16 max-[600px]:items-start max-[600px]:px-4 max-[600px]:pt-7 max-[600px]:pb-10">
        <div className="w-full max-w-140 rounded-xl border border-card-border bg-white px-14 pt-11 pb-12 text-center shadow-[0_1px_3px_rgba(51,51,51,0.04)] max-[600px]:rounded-[10px] max-[600px]:px-6 max-[600px]:py-8">
          <img
            className="mx-auto mb-5 block h-auto w-42.5 max-w-[60%] opacity-60 max-[600px]:mb-3.5 max-[600px]:w-32"
            src={GIFTBOX_SRC}
            alt=""
            aria-hidden="true"
            width={170}
          />

          <h1 className="[hyphens:none] mb-5 text-balance font-crimson font-normal text-[40px] text-rodeo-black leading-[1.2] tracking-[-0.2px] max-[600px]:mb-4 max-[600px]:text-[30px]">
            {headline}
          </h1>

          <p className="mx-auto max-w-[46ch] text-balance font-lato text-[17px] text-graphite leading-[1.6] max-[600px]:text-[16px]">
            {body}
          </p>

          <hr className="my-8 h-px border-0 bg-card-border max-[600px]:my-6.5" />

          <p className="m-0 text-pretty font-lato text-[13px] text-graphite leading-[1.6]">
            Still need help? Email{" "}
            <a
              href="mailto:hello@giftsforgood.com"
              className="whitespace-nowrap border-transparent border-b font-bold text-dust-ink no-underline transition-colors hover:border-dark-dust focus-visible:rounded-xs focus-visible:border-dark-dust focus-visible:outline-2 focus-visible:outline-dark-dust focus-visible:outline-offset-3"
            >
              hello@giftsforgood.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
